import { FilterOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Dropdown,
  Flex,
  Select,
  Form,
  TreeSelect,
} from "antd";
import { useBoolean } from "../../hooks/use-boolean";
import { lateTypesOptions } from "../../utils/constants";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import axiosInstance, { endpoints } from "../../services/axios";
import { useSearchParams } from "react-router-dom";
import { get } from "lodash";
import { getCurrentMonthRange } from "../../utils/dateHelpers";

const { TreeNode } = TreeSelect;
const { RangePicker } = DatePicker;

const Filter = ({ setPeriod, setIds }: any) => {
  const [searchParams] = useSearchParams();
  const isOpen = useBoolean();
  const isManual = useBoolean(false);
  const [form] = Form.useForm();

  const handleApplyFilters = (values: any) => {
    localStorage.setItem("EMPLOYEE_ATTENDANCE_FILTER", JSON.stringify(values));
    setIds({
      companyIds: values.companyIds,
      departmentIds: values.departmentIds,
    });

    const today = new Date();
    let from: Date;
    let to: Date;

    const getStartOfWeek = (date: any) => {
      const d = new Date(date);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      return new Date(d.setDate(diff));
    };
    const getEndOfWeek = (startOfWeek: Date) => {
      const end = new Date(startOfWeek);
      end.setDate(startOfWeek.getDate() + 6);
      return end;
    };

    switch (values.filterType) {
      case "TODAY":
        from = to = today;
        break;
      case "CURRENT_WEEK":
        from = getStartOfWeek(today);
        to = getEndOfWeek(from);
        break;
      case "LAST_WEEK": {
        const lastWeekStart = getStartOfWeek(
          new Date(today.setDate(today.getDate() - 7))
        );
        from = lastWeekStart;
        to = getEndOfWeek(lastWeekStart);
        break;
      }
      case "CURRENT_MONTH":
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case "LAST_MONTH":
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "MANUAL":
        from = dayjs(values.date[0]).add(1, "day").startOf("day").toDate();
        to = dayjs(values.date[1]).endOf("day").toDate();
        break;
      default:
        return;
    }

    const formatDate = (date: any) => dayjs(date).format("YYYY-MM-DD");
    setPeriod({ fromDate: formatDate(from), toDate: formatDate(to) });
  };

  useEffect(() => {
    const stored = localStorage.getItem("EMPLOYEE_ATTENDANCE_FILTER");
    if (!stored) return;

    try {
      const parsed = JSON.parse(stored);

      form.setFieldsValue({
        ...parsed,
        date:
          parsed?.date && parsed.date.length === 2
            ? [dayjs(parsed.date[0]), dayjs(parsed.date[1])]
            : undefined,
      });

      isManual.setValue(parsed.filterType === "MANUAL");
    } catch (err) {
      console.error("Failed to load filters from storage", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: filterData } = useQuery({
    queryKey: ["employee-attendance-filter"],
    queryFn: async () => {
      axiosInstance.defaults.headers.common.Authorization =
        searchParams.get("token");
      return await axiosInstance.get(endpoints.employeeAttendance.filter);
    },
  });

  const departments = get(filterData, "data.data.departments.options", []);
  const companies = get(filterData, "data.data.companies.options", []);
  const cDefVal: string = get(filterData, "data.data.companies.value", "");

  const mapTreeData = (nodes: any) => {
    return nodes?.map((node: any) => (
      <TreeNode key={node?.id} title={node?.name} value={node?.id}>
        {node?.children && mapTreeData(node?.children)}
      </TreeNode>
    ));
  };

  const dropdownContent = (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleApplyFilters}
      initialValues={{
        clearCache: false,
        showResignEmployee: false,
        date: dayjs(),
      }}
    >
      <Flex
        vertical
        gap={10}
        className="rounded-md border border-gray-300 bg-white p-4 shadow"
      >
        <Flex gap={10} vertical>
          <Form.Item label="Sana tanlash" name="filterType" className="mb-1">
            <Select
              className="mb-1 w-[350px]"
              popupMatchSelectWidth={false}
              options={lateTypesOptions}
              allowClear
              defaultValue={lateTypesOptions[0].value}
              placeholder="Choose date or options"
              onChange={(e) => {
                isManual.setValue(e === "MANUAL");
              }}
            />
          </Form.Item>
          {isManual.value && (
            <Form.Item label="From  To " name="date" className="mb-1 w-[350px]">
              <RangePicker
                className="w-full"
                showTime={false}
                format="YYYY-MM-DD"
                onCalendarChange={(e) => {
                  console.log("value e", e);
                }}
              />
            </Form.Item>
          )}
        </Flex>

        <Form.Item
          label="Department"
          name="departmentIds"
          className="mb-1 w-[350px]"
        >
          <TreeSelect
            allowClear
            showCheckedStrategy={TreeSelect.SHOW_ALL}
            treeCheckable={true}
            multiple
            maxTagCount={2}
            listHeight={400}
            treeDefaultExpandedKeys={["1"]}
            placeholder="Choose departments!"
          >
            {mapTreeData(departments)}
          </TreeSelect>
        </Form.Item>

        <Form.Item label="Company" name="companyIds" className="mb-1 w-[350px]">
          <Select
            allowClear
            mode="multiple"
            placeholder="Choose companies!"
            options={companies?.map((company: any) => ({
              label: company.name,
              value: company.id,
            }))}
            defaultValue={
              cDefVal
                ? ((): any => {
                    const found = companies.find((c: any) => c.id === cDefVal);
                    return found
                      ? [{ label: found.name, value: found.id }]
                      : undefined;
                  })()
                : undefined
            }
          />
        </Form.Item>

        {/*<Form.Item label="Company" name="companyIds" className="mb-1 w-[350px]">*/}
        {/*  <TreeSelect allowClear treeCheckable={true} multiple listHeight={400}  placeholder="Choose companies!" >*/}
        {/*    {mapTreeData(companies)}*/}
        {/*  </TreeSelect>*/}
        {/*</Form.Item>*/}

        <Flex className="gap-4">
          <Button className="w-full" htmlType="submit" type="primary">
            Apply filter
          </Button>
          <Button
            danger
            className="w-full"
            onClick={() => {
              localStorage.removeItem("EMPLOYEE_ATTENDANCE_FILTER");
              form.resetFields();
              setIds({});
              setPeriod(getCurrentMonthRange());
            }}
          >
            Clear filter
          </Button>
        </Flex>
      </Flex>
    </Form>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={["click"]}
      placement="bottomRight"
      open={isOpen.value}
      onOpenChange={() => isOpen.onToggle()}
    >
      <Button icon={<FilterOutlined />} type="primary">
        Filter
      </Button>
    </Dropdown>
  );
};

export default Filter;
